import type React from 'react';
import {
  Pause,
  X,
  CircleDot,
  CheckCircle2,
  AlertCircle,
  Clock,
} from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

export interface RunLogEntry {
  ts: string;
  level: 'info' | 'warn' | 'error';
  msg: string;
}

export interface IngestionConfig {
  id: string;
  storeId: number;
  platform: string;
  status: 'active' | 'paused' | 'disabled' | 'backfilling';
  timezone: string;
  webhookEnabled: boolean;
  scheduleCron: string;
  backfillCursor: string | null;
  backfillTarget: string | null;
  lastWebhookAt: string | null;
  lastScheduledAt: string | null;
  lastBackfillAt: string | null;
  lastStoreActiveAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigDetail extends IngestionConfig {
  recentRuns: IngestionRun[];
}

export interface WindowCheckpoint {
  start: string;
  end: string;
  status: 'completed' | 'errored' | 'pending';
  rowsFromCollector: number;
  rowsInClickHouse: number | null;
  attempt: number;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  errorMessage?: string;
}

export interface IngestionRun {
  id: string;
  storeId: number;
  platform: string;
  runType: 'scheduled' | 'backfill' | 'manual' | 'webhook_catchup';
  triggerSource: 'system' | 'admin' | 'mcp' | 'event' | 'schedule' | 'gap-backfill' | 'store-active';
  status: 'running' | 'completed' | 'errored' | 'awaiting_reports' | 'partial';
  startedAt: string;
  completedAt: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  rowsIngested: number;
  errorMessage: string | null;
  logs: RunLogEntry[];
  idempotencyKey?: string;
  durationMs?: number | null;
  rowsWritten?: Record<string, number>;
  windowsCompleted?: number;
  windowsTotal?: number;
  backfillGroupId?: string | null;
  jobId?: string | null;
  windowCheckpoints?: WindowCheckpoint[];
  createdAt: string;
}

export interface StoreListItem {
  id: number;
  name: string;
  type: string;
  currency: string;
  owner: { email: string; displayName: string | null } | null;
  integrations: string[];
}

export interface MonthCell {
  month: string;
  status: 'completed' | 'errored' | 'running' | 'partial' | 'empty';
  rows: number;
  runCount: number;
  activeJobs?: number;
  lastRunAt: string | null;
  errorMessage: string | null;
}

export interface DayRunSummary {
  id: string;
  runType: string;
  triggerSource: string;
  status: string;
  rowsIngested: number;
  startedAt: string;
  completedAt: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  errorMessage: string | null;
}

export interface DayCell {
  date: string;
  status: 'completed' | 'errored' | 'running' | 'partial' | 'empty' | 'future';
  rowCount?: number;
  attempts?: number;
  lastError?: string | null;
  lastRunId?: string | null;
  runs?: DayRunSummary[];
}

/* ─── Backfill Group Types ────────────────────── */

export interface BackfillGroupMonthSummary {
  month: string | null;
  status: string;
  rowsIngested: number;
  windowsCompleted: number;
  windowsTotal: number;
  errorMessage: string | null;
}

export interface BackfillGroup {
  groupId: string;
  storeId: number;
  platform: string;
  status: 'running' | 'completed' | 'errored' | 'partial';
  jobCount: number;
  completed: number;
  errored: number;
  running: number;
  retries?: number;
  totalRows: number;
  totalWindows: number;
  completedWindows: number;
  dateRange: { start: string | null; end: string | null };
  startedAt: string;
  lastActivity: string;
  durationMs: number;
  months: BackfillGroupMonthSummary[];
}

export interface QueueJob {
  id: string;
  name: string;
  data: Record<string, unknown>;
  failedReason?: string;
  stacktrace?: string[];
  attemptsMade?: number;
  maxAttempts?: number;
  backoff?: { type: string; delay: number } | null;
  processedOn?: number;
  timestamp?: number;
  finishedOn?: number;
  progress?: number;
}

export interface QueueStatusData {
  queueName: string;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
  failed: QueueJob[];
  completed: QueueJob[];
  waiting: QueueJob[];
  active: QueueJob[];
  delayed: QueueJob[];
}

/* ─── Amazon Types ────────────────────────────── */

export interface ReportDetail {
  reportType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  amazonReportId?: string;
  createdAt: string;
  completedAt?: string;
  duration?: number;
  rowCount?: number;
  errorMessage?: string;
  lastPolledAt?: string;
  pollAttempts?: number;
  amazonStatus?: string;
}

export interface AmazonConfigData {
  marketplace: string;
  profileId?: string | null;
  enabledReportTypes: Record<string, boolean>;
  recentReports: ReportDetail[];
}

/** Flat report returned by GET /admin/amazon-reports */
export interface AmazonReportRow extends ReportDetail {
  runId: string;
  storeId: number;
  platform: string;
  windowStart: string | null;
  windowEnd: string | null;
  triggerSource: string;
  runStatus: string;
}

export interface AmazonReportsResponse {
  reports: AmazonReportRow[];
  stats: {
    total: number;
    processing: number;
    completed: number;
    failed: number;
    pending: number;
  };
  total: number;
  limit: number;
  offset: number;
}

export const REPORT_TYPE_LABELS: Record<string, string> = {
  salesAndTraffic: 'Sales & Traffic',
  orderLevelData: 'Order Data',
  sp: 'Sponsored Products',
  sb: 'Sponsored Brands',
  sd: 'Sponsored Display',
  dsp: 'DSP',
};

export interface AmazonMarketplace {
  code: string;
  name: string;
}

export const AMAZON_MARKETPLACES: AmazonMarketplace[] = [
  { code: 'US', name: 'United States' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'CA', name: 'Canada' },
  { code: 'TR', name: 'Turkey' },
];

export interface ReportToggleDef {
  key: string;
  label: string;
  description: string;
  defaultEnabled: boolean;
}

export const SELLER_REPORT_TOGGLES: ReportToggleDef[] = [
  { key: 'salesAndTraffic', label: 'Sales & Traffic', description: 'Daily revenue, units, sessions, page views', defaultEnabled: true },
  { key: 'orderLevelData', label: 'Order-Level Data', description: 'Individual orders from SP-API flat file report', defaultEnabled: true },
];

export const ADS_REPORT_TOGGLES: ReportToggleDef[] = [
  { key: 'sp', label: 'Sponsored Products', description: 'Product-level campaign performance', defaultEnabled: true },
  { key: 'sb', label: 'Sponsored Brands', description: 'Brand awareness campaign metrics', defaultEnabled: true },
  { key: 'sd', label: 'Sponsored Display', description: 'Display and retargeting campaigns', defaultEnabled: true },
  { key: 'dsp', label: 'DSP', description: 'Demand Side Platform programmatic ads', defaultEnabled: false },
];

/* ─── Constants ───────────────────────────────── */

export const STATUS_CONFIG: Record<string, { icon: React.ElementType; className: string; label: string }> = {
  active: { icon: CheckCircle2, className: 'text-green-600 bg-green-50 border-green-200', label: 'Active' },
  backfilling: { icon: CircleDot, className: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Backfilling' },
  paused: { icon: Pause, className: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Paused' },
  disabled: { icon: X, className: 'text-gray-400 bg-gray-50 border-gray-200', label: 'Disabled' },
  running: { icon: CircleDot, className: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Running' },
  completed: { icon: CheckCircle2, className: 'text-green-600 bg-green-50 border-green-200', label: 'Completed' },
  errored: { icon: AlertCircle, className: 'text-red-600 bg-red-50 border-red-200', label: 'Errored' },
  partial: { icon: AlertCircle, className: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Partial' },
  awaiting_reports: { icon: Clock, className: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Awaiting Reports' },
};

export const RUN_TYPE_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  backfill: 'Backfill',
  manual: 'Manual',
  webhook_catchup: 'Webhook Catchup',
};

export const TRIGGER_SOURCE_LABELS: Record<string, { label: string; className: string }> = {
  admin: { label: 'Manual', className: 'bg-purple-100 text-purple-700' },
  mcp: { label: 'MCP', className: 'bg-indigo-100 text-indigo-700' },
  event: { label: 'Auto (event)', className: 'bg-blue-100 text-blue-700' },
  schedule: { label: 'Auto (cron)', className: 'bg-gray-100 text-gray-600' },
  'gap-backfill': { label: 'Auto (gap)', className: 'bg-amber-100 text-amber-700' },
  'store-active': { label: 'Store Active', className: 'bg-teal-100 text-teal-700' },
  system: { label: 'System', className: 'bg-gray-100 text-gray-500' },
};

/* ─── Helpers ─────────────────────────────────── */

export function formatRows(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function formatDuration(ms: number): string {
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m`;
}

export function cronToHuman(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return cron;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const pad = (v: string) => v!.padStart(2, '0');

  // "30 23 * * *" → "Daily at 23:30 UTC"
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    if (minute !== '*' && hour !== '*') {
      return `Daily at ${pad(hour!)}:${pad(minute!)} UTC`;
    }
    if (hour === '*' && minute !== '*') {
      return `Every hour at :${pad(minute!)}`;
    }
    if (hour === '*' && minute === '*') {
      return 'Every minute';
    }
  }

  // "0 6 * * 1" → "Mondays at 06:00 UTC"
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const dayNum = parseInt(dayOfWeek!, 10);
    const dayName = dayNames[dayNum] ?? dayOfWeek;
    if (minute !== '*' && hour !== '*') {
      return `${dayName} at ${pad(hour!)}:${pad(minute!)} UTC`;
    }
  }

  // "0 */4 * * *" → "Every 4 hours"
  if (minute === '0' && hour?.startsWith('*/')) {
    return `Every ${hour.slice(2)} hours`;
  }

  // "*/15 * * * *" → "Every 15 minutes"
  if (minute?.startsWith('*/') && hour === '*') {
    return `Every ${minute.slice(2)} minutes`;
  }

  return cron;
}

export function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m!, 10) - 1]} ${y!.slice(2)}`;
}
